import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon, BanIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const FINALIDADES_FILA = ["DESCARREGAR_CHEIOS", "CARREGAR_VAZIOS", "DESCARREGAR_BACKLOAD", "CARREGAR_LOAD", "CARRETA_VAZIA_STANDBY"];
export const FINALIDADES = {
  DESCARREGAR_CHEIOS: "DESCARREGAR CHEIOS",
  CARREGAR_VAZIOS: "CARREGAR VAZIOS",
  DESCARREGAR_BACKLOAD: "DESCARREGAR BACKLOAD",
  CARREGAR_LOAD: "CARREGAR LOAD",
  CARRETA_VAZIA_STANDBY: "CARRETA VAZIA / STANDBY",
};
export const FIN_CURTO = { DESCARREGAR_CHEIOS: "CHEIOS", CARREGAR_VAZIOS: "VAZIOS", DESCARREGAR_BACKLOAD: "BACKLOAD", CARREGAR_LOAD: "LOAD", CARRETA_VAZIA_STANDBY: "STANDBY" };
export const FIN_COR = { DESCARREGAR_CHEIOS: "#00E5FF", CARREGAR_VAZIOS: "#FFD60A", DESCARREGAR_BACKLOAD: "#FF9F0A", CARREGAR_LOAD: "#34C759", CARRETA_VAZIA_STANDBY: "#C7C7CC" };
const EM_ANDAMENTO_LABEL = {
  DESCARREGAR_CHEIOS: "DESCARREGANDO CHEIOS",
  CARREGAR_VAZIOS: "CARREGANDO VAZIOS",
  DESCARREGAR_BACKLOAD: "DESCARREGANDO BACKLOAD",
  CARREGAR_LOAD: "CARREGANDO LOAD",
  CARRETA_VAZIA_STANDBY: "EM STANDBY (CARRETA VAZIA)",
};

const motivoCancel = (op, f) =>
  op?.cancelamentos?.[f]?.motivo || (f === "CARREGAR_VAZIOS" ? op?.vazios_cancelado_motivo : null);

const fins = (op) => (op?.finalidades || [op?.finalidade]).filter(Boolean);

/** Resume a etapa atual do veículo (fila EM_OPERACAO) para Telão / Portaria / Balança. */
export function etapaInfo(op) {
  if (!op) return null;
  const et = op.etapas || {};
  const previstas = fins(op);
  const pendentes = previstas.filter((f) => f !== op.etapa_atual && (et[f] || "AGUARDANDO") === "AGUARDANDO");
  const canceladas = previstas.filter((f) => et[f] === "CANCELADO");
  const partes = [
    ...pendentes.map((f) => `depois ${FIN_CURTO[f]}`),
    ...canceladas.map((f) => {
      const m = motivoCancel(op, f);
      return `${FIN_CURTO[f]}: NÃO FAZ${m ? ` (${m})` : ""}`;
    }),
  ];
  if (op.etapa_atual && EM_ANDAMENTO_LABEL[op.etapa_atual]) {
    const concluidas = previstas.filter((f) => et[f] === "CONCLUIDO").map((f) => `${FIN_CURTO[f]} ✓`);
    return {
      label: EM_ANDAMENTO_LABEL[op.etapa_atual],
      sub: [...concluidas, ...partes].join(" · ") || null,
      cor: FIN_COR[op.etapa_atual] || "#00E5FF",
    };
  }
  const concluidas = previstas.filter((f) => et[f] === "CONCLUIDO");
  if (concluidas.length) {
    return {
      label: `${FIN_CURTO[concluidas[concluidas.length - 1]]} OK`,
      sub: partes.join(" · ") || null,
      cor: "#34C759",
    };
  }
  return { label: "EM OPERAÇÃO", sub: partes.join(" · ") || null, cor: "#00E5FF" };
}

export function EtapaBadge({ op, size = "sm", testid }) {
  const info = etapaInfo(op);
  if (!info) return null;
  const big = size === "lg";
  return (
    <div data-testid={testid} className={`inline-flex flex-col ${big ? "gap-0.5" : ""}`}>
      <span className={`font-mono font-black uppercase ${big ? "text-sm px-3 py-1" : "text-[10px] px-2 py-0.5"} border`}
            style={{ color: info.cor, borderColor: `${info.cor}66`, background: `${info.cor}14` }}>
        {info.label}
      </span>
      {info.sub && <span className={`${big ? "text-xs" : "text-[10px]"} text-white/50 uppercase`}>→ {info.sub}</span>}
    </div>
  );
}

/** Botões de etapa (seguir p/ cheios / vazios / backload e cancelar etapa) — Portaria e Balança usam os mesmos. */
export function EtapaActions({ op, onDone, compact = false }) {
  if (!op || op.status !== "EM_OPERACAO") return null;
  const et = op.etapas || {};
  const previstas = fins(op);
  const finalizado = (f) => ["CONCLUIDO", "CANCELADO", "EM_ANDAMENTO"].includes(et[f]);
  const podeSeguir = FINALIDADES_FILA.filter((f) => !finalizado(f));
  const podeCancelar = previstas.filter((f) => !["CONCLUIDO", "CANCELADO"].includes(et[f]));
  if (!podeSeguir.length && !podeCancelar.length) return null;

  const enviar = async (acao, finalidade) => {
    let motivo = null;
    if (acao === "CANCELAR") {
      motivo = window.prompt(`${op.nome} NÃO vai ${FINALIDADES[finalidade].toLowerCase()}. Informe o motivo (ex.: embalagem recusada no recebimento):`, "");
      if (motivo === null) return;
    }
    try {
      await api.post(`/portaria/aguardando/${op.id}/etapa`, { acao, finalidade, motivo });
      toast.success(acao === "SEGUIR"
        ? `${op.nome} seguiu para ${FINALIDADES[finalidade].toLowerCase()}`
        : `${op.nome}: ${FINALIDADES[finalidade].toLowerCase()} cancelado`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const h = compact ? "h-7 text-[10px]" : "h-9 text-xs";
  return (
    <div className="flex gap-2 flex-wrap">
      {podeSeguir.map((f) => (
        <Button key={f} size="sm" onClick={() => enviar("SEGUIR", f)} data-testid={`etapa-seguir-${f}-${op.id}`}
                title={`Seguiu para ${FINALIDADES[f].toLowerCase()}`}
                className={`rounded-none text-black ${h} font-bold uppercase tracking-wider`}
                style={{ background: FIN_COR[f] }}>
          <ArrowRightIcon className="w-3.5 h-3.5 mr-1" /> Seguiu p/ {FIN_CURTO[f]}
        </Button>
      ))}
      {podeCancelar.map((f) => (
        <Button key={f} size="sm" variant="outline" onClick={() => enviar("CANCELAR", f)} data-testid={`etapa-cancelar-${f}-${op.id}`}
                title={`Não vai ${FINALIDADES[f].toLowerCase()}`}
                className={`rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 ${h} font-bold uppercase tracking-wider`}>
          <BanIcon className="w-3.5 h-3.5 mr-1" /> Não faz {FIN_CURTO[f]}
        </Button>
      ))}
    </div>
  );
}
